import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useI18n } from '../i18n';
import { client } from '../api/client';

interface SavedSearch {
  id: number;
  name: string;
  filters: Record<string, any>;
  page_type: 'offers' | 'demands' | 'auctions';
  created_at: string;
}

export function SavedSearchesPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isChecking, setFilters } = useAppStore();
  const { language } = useI18n();
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (isChecking) return; // Wait for auth check
    if (!isAuthenticated) {
      navigate('/prijava');
      return;
    }
    loadSearches();
  }, [isAuthenticated, isChecking, navigate]);

  const loadSearches = async () => {
    try {
      const res = await client.api.fetch('/api/saved-searches');
      const data = await res.json();
      if (data.success) {
        setSearches(data.data || []);
      }
    } catch (err) {
      console.error('Failed to load saved searches:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(language === 'sr' ? 'Da li ste sigurni da želite da obrišete ovu pretragu?' : 'Are you sure you want to delete this search?')) {
      return;
    }
    
    setDeleting(id);
    try {
      const res = await client.api.fetch(`/api/saved-searches/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setSearches(searches.filter(s => s.id !== id));
        setMessage({ type: 'success', text: language === 'sr' ? 'Pretraga obrisana' : 'Search deleted' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Error' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: language === 'sr' ? 'Greška pri brisanju' : 'Error deleting' });
    } finally {
      setDeleting(null);
    }
  };

  const handleApply = (search: SavedSearch) => {
    // Apply the saved filters
    setFilters(search.filters);
    // Navigate to the appropriate page
    const path = search.page_type === 'offers' ? '/ponuda' 
      : search.page_type === 'demands' ? '/potraznja' 
      : '/aukcije';
    navigate(path);
  };

  const getPageTypeLabel = (type: string) => {
    if (type === 'offers') return language === 'sr' ? 'Ponuda' : 'Offers';
    if (type === 'demands') return language === 'sr' ? 'Potražnja' : 'Demands';
    return language === 'sr' ? 'Aukcije' : 'Auctions';
  };

  const formatFilters = (filters: Record<string, any>) => {
    const parts: string[] = [];
    if (filters.region) parts.push(filters.region);
    if (filters.city) parts.push(filters.city);
    if (filters.category) parts.push(filters.category);
    if (filters.adType) parts.push(filters.adType);
    if (filters.priceMin || filters.priceMax) {
      parts.push(`${filters.priceMin || 0} - ${filters.priceMax || '∞'} EUR`);
    }
    return parts.length > 0 ? parts.join(' • ') : (language === 'sr' ? 'Svi filteri' : 'All filters');
  };

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
              {language === 'sr' ? 'Moje sačuvane pretrage' : 'My Saved Searches'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {language === 'sr' 
                ? 'Brzo pristupite vašim sačuvanim pretragama' 
                : 'Quickly access your saved searches'}
            </p>
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
              {message.text}
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-12 text-center">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400">
                {language === 'sr' ? 'Učitavanje...' : 'Loading...'}
              </p>
            </div>
          ) : searches.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Nemate sačuvanih pretraga' : 'No saved searches'}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {language === 'sr' 
                  ? 'Pretražujte nekretnine i sačuvajte pretrage za brži pristup' 
                  : 'Search for properties and save searches for quick access'}
              </p>
              <Link
                to="/ponuda"
                className="inline-flex items-center gap-2 bg-amber-500 text-white font-semibold py-3 px-6 rounded-lg hover:bg-amber-400 transition-colors"
              >
                {language === 'sr' ? 'Pretraži nekretnine' : 'Search properties'}
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {searches.map((search) => (
                <motion.div
                  key={search.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6"
                >
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                          {search.name}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          search.page_type === 'offers' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                          search.page_type === 'demands' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {getPageTypeLabel(search.page_type)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                        {formatFilters(search.filters)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {language === 'sr' ? 'Sačuvano:' : 'Saved:'} {new Date(search.created_at).toLocaleDateString(language === 'sr' ? 'sr-RS' : 'en-US')}
                      </p>
                    </div>
                    <div className="flex gap-2 sm:flex-col">
                      <button
                        onClick={() => handleApply(search)}
                        className="flex-1 sm:flex-none px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors text-sm"
                      >
                        {language === 'sr' ? 'Primeni' : 'Apply'}
                      </button>
                      <button
                        onClick={() => handleDelete(search.id)}
                        disabled={deleting === search.id}
                        className="flex-1 sm:flex-none px-4 py-2 border border-red-300 text-red-600 rounded-lg font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm disabled:opacity-50"
                      >
                        {deleting === search.id ? '...' : (language === 'sr' ? 'Obriši' : 'Delete')}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
